import { AxiosError } from 'axios';
import { api } from '@shared/api';
import { parseGenerationDetail, parseGenerationsPage } from '../lib/parse';
import type { GenerationDetail, GenerationsPage } from '../model/types';

/** 404 `GENERATION_NOT_FOUND`: записи нет, она чужая или её модуль удалён либо скрыт.
 *  Для проверки после обрыва связи (ТЗ § 10) это «карточки не создались». */
export class GenerationNotFoundError extends Error {
  constructor() {
    super('GENERATION_NOT_FOUND');
    this.name = 'GenerationNotFoundError';
  }
}

/** `GET /generations` — свёрнутые записи, новые сверху; курсор следующей страницы или null. */
export async function fetchGenerations({
  cursor,
  limit,
  signal,
}: { cursor?: string | null; limit?: number; signal?: AbortSignal } = {}): Promise<GenerationsPage> {
  const params: Record<string, string | number> = {};
  if (limit) params.limit = limit;
  if (cursor) params.cursor = cursor;
  const { data } = await api.get<unknown>('/generations', { params, signal });
  return parseGenerationsPage(data);
}

/**
 * `GET /generations?ids=a,b` (контракт § 7.2, этап 4) — сводки названных записей, до 50: опрос отслеживаемых
 * записей за первой страницей. Невидимых записей (модуль удалён или скрыт) в ответе нет. Строка через запятую:
 * массив axios по умолчанию пишет `ids[]=…`, и такой ключ сервер не узнаёт (notes.md).
 */
export async function fetchGenerationsByIds(ids: readonly string[], signal?: AbortSignal): Promise<GenerationsPage> {
  const { data } = await api.get<unknown>('/generations', { params: { ids: ids.join(',') }, signal });
  return parseGenerationsPage(data);
}

/** `GET /generations/{id}` — сводка и элементы с содержимым карточек, включая пропуски. */
export async function fetchGeneration(id: string, signal?: AbortSignal): Promise<GenerationDetail> {
  let data: unknown;
  try {
    ({ data } = await api.get<unknown>(`/generations/${encodeURIComponent(id)}`, { signal }));
  } catch (err) {
    if (err instanceof AxiosError && err.response?.status === 404) throw new GenerationNotFoundError();
    throw err;
  }
  const detail = parseGenerationDetail(data);
  if (!detail) throw new Error('GENERATION_BAD_RESPONSE');
  return detail;
}
